import { useState } from "react";
import { Save, User, Lock } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { crmInput, Field } from "../../components/crm";
import { toast } from "sonner";

export default function ClientProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "", company: user?.company || "",
    website: user?.website || "", industry: user?.industry || "", address: user?.address || "", notes: user?.notes || "",
  });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setP = (k, v) => setPw((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await api.put("/auth/me", form);
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save profile");
    }
  };

  const savePw = async () => {
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    try {
      await api.put("/auth/password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="max-w-2xl" data-testid="client-profile">
      <h1 className="font-heading font-extrabold text-3xl text-brand-purpleDeep">My profile</h1>
      <p className="mt-1 text-brand-purpleDeep/50">Update your personal, business and project details.</p>

      <div className="mt-6 bg-white rounded-2xl border border-border p-6 sm:p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-brand-purple text-white flex items-center justify-center font-heading font-extrabold text-xl">{user?.name?.[0] || <User />}</div>
          <div><div className="font-heading font-bold text-brand-purpleDeep">{user?.email}</div><div className="text-sm text-brand-purpleDeep/50 capitalize">{user?.role} account</div></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><input className={crmInput} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" /></Field>
          <Field label="Company"><input className={crmInput} value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="profile-company" /></Field>
          <Field label="Phone"><input className={crmInput} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" /></Field>
          <Field label="Website"><input className={crmInput} placeholder="yourbusiness.co.nz" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="profile-website" /></Field>
          <Field label="Industry"><input className={crmInput} value={form.industry} onChange={(e) => set("industry", e.target.value)} data-testid="profile-industry" /></Field>
          <Field label="Address"><input className={crmInput} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="profile-address" /></Field>
          <div className="sm:col-span-2"><Field label="About your business / project notes"><textarea rows={3} className={crmInput} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="profile-notes" /></Field></div>
        </div>
        <div className="mt-6 flex justify-end"><button onClick={save} className="inline-flex items-center gap-2 bg-brand-purple hover:bg-brand-purpleDark text-white font-semibold px-5 py-2.5 rounded-lg" data-testid="save-profile"><Save size={16} /> Save changes</button></div>
      </div>

      {/* Manage password */}
      <div className="mt-6 bg-white rounded-2xl border border-border p-6 sm:p-8" data-testid="manage-password">
        <div className="flex items-center gap-2 mb-5"><Lock size={18} className="text-brand-orange" /><h2 className="font-heading font-bold text-brand-purpleDeep">Manage password</h2></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Current password"><input type="password" className={crmInput} value={pw.current_password} onChange={(e) => setP("current_password", e.target.value)} data-testid="pw-current" /></Field>
          <Field label="New password"><input type="password" className={crmInput} value={pw.new_password} onChange={(e) => setP("new_password", e.target.value)} data-testid="pw-new" /></Field>
          <Field label="Confirm new"><input type="password" className={crmInput} value={pw.confirm} onChange={(e) => setP("confirm", e.target.value)} data-testid="pw-confirm" /></Field>
        </div>
        <div className="mt-5 flex justify-end"><button onClick={savePw} className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold px-5 py-2.5 rounded-lg" data-testid="save-password"><Lock size={15} /> Update password</button></div>
      </div>
    </div>
  );
}
